import {
  columnFilteringFeature,
  createFilteredRowModel,
  createSortedRowModel,
  functionalUpdate,
  globalFilteringFeature,
  rowSortingFeature,
  tableFeatures,
  useTable,
} from '@tanstack/react-table';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ChevronLeft, ChevronRight, Search, SearchX } from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { formatCount } from '../lib/format.ts';
import { isTypingTarget } from '../lib/platform.ts';
import { columnAlign, nameColumn, sortsDescFirst, tableClass } from '../lib/table.ts';
import type { CellAlign, ColumnType } from '../lib/table.ts';
import { TableSortButton } from '../primitives/table.tsx';
import { Button } from './Button.tsx';
import { EmptyState } from './EmptyState.tsx';
import { ErrorState } from './ErrorState.tsx';
import { IconButton } from './IconButton.tsx';
import { Input } from './Input.tsx';
import { Kbd } from './Kbd.tsx';
import { REVEAL_GROUP, RevealSlot } from './Reveal.tsx';
import { Skeleton } from './Skeleton.tsx';

/**
 * The one table, on TanStack Table (sorting and search) and TanStack Virtual (long lists).
 * The data drives alignment and sort direction (numbers right in `num`, biggest first); exactly
 * one column grows; every other column fits its content on one line. The table owns its
 * search, filters, grouping and paging, and renders all five states (components.md,
 * Collections and The five states).
 */
export type { ColumnType } from '../lib/table.ts';

export interface Column<T> {
  key: string;
  header: string;
  /**
   * What the column holds. Numbers, money, durations and percentages align right with tabular
   * numerals. Left out, the data decides: a sort value that is a number on every row aligns right.
   */
  type?: ColumnType;
  /** Declares the column numeric (right, `num`) or not; wins over the type and the data. */
  numeric?: boolean;
  cell: (row: T) => ReactNode;
  /** Text the search matches against. Defaults to nothing for this column. */
  text?: (row: T) => string;
  /** Comparable value; the column is sortable when present. */
  sortValue?: (row: T) => string | number | null | undefined;
  /** The identity column: absorbs the slack. Exactly one per table. */
  grow?: boolean;
  align?: CellAlign;
  /** A summary cell for the dim footer row (a total); the footer shows when any column has one. */
  footer?: (rows: T[]) => ReactNode;
  /** Hide on narrow screens. */
  hideBelow?: 'sm' | 'md' | 'lg';
  /** Header read by screen readers only. */
  srHeader?: boolean;
  className?: string;
}

export interface DataTableProps<T> {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  /** Accessible name of the table. */
  label: string;
  searchable?: { placeholder: string; /** Focus the search with "/" while this table is on screen. */ shortcut?: boolean };
  /** Filter controls on the table's top edge, beside the search. */
  filters?: ReactNode;
  /** Trailing toolbar content (a count, a small action). */
  toolbarEnd?: ReactNode;
  /** Controls acting on one row; revealed with the row. */
  rowActions?: (row: T) => ReactNode;
  onRowClick?: (row: T) => void;
  /** Accessible name of the row's click action, for example "Open audit entry". */
  rowLabel?: (row: T) => string;
  /** Group rows: a header row precedes each group. Groups keep first-seen order unless `groupSort` is given. */
  groupBy?: (row: T) => string;
  renderGroup?: (key: string, rows: T[]) => ReactNode;
  groupSort?: (a: string, b: string) => number;
  /** Rows per page; false shows everything. Default 25 (components.md suggests 50 for long histories). */
  pageSize?: number | false;
  /** Keyset paging for histories that grow at the head. */
  loadMore?: { onLoad: () => void; loading: boolean; more: boolean };
  /** Shown when there are no rows at all (not when a search hides them). */
  empty?: ReactNode;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void | Promise<void>;
  initialSort?: { key: string; dir: 'asc' | 'desc' };
  /** Highlights a row (for example a deep link). */
  activeKey?: string | null;
  className?: string;
  /** Frameless: no border or surface (the parent is the card). */
  frameless?: boolean;
  /**
   * Render only the rows in view once more than this many are on screen (default 100, the
   * components.md threshold). The body then scrolls inside the table under a sticky header.
   */
  virtualizeAbove?: number;
}

const HIDE: Record<NonNullable<Column<unknown>['hideBelow']>, string> = {
  sm: 'hidden sm:table-cell',
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
};
/** The hidden column the search runs against: every column's `text`, joined and lowercased. */
const SEARCH = '__search';

const features = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  columnFilteringFeature,
  globalFilteringFeature,
  filteredRowModel: createFilteredRowModel(),
});
type Features = typeof features;

/** Ascending order; missing values last. TanStack reverses it for descending. */
export function compareValues(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'en-US', { numeric: true, sensitivity: 'base' });
}

/** Every word of the query appears somewhere in the row's searchable text. */
export function matchesQuery(haystack: string, query: string): boolean {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return words.every((w) => haystack.includes(w));
}

/** TanStack wants object rows; ours may be any type, so each row rides in a box. */
type Boxed<T> = { v: T };

type DisplayItem<T> = { kind: 'group'; key: string; rows: T[] } | { kind: 'row'; key: string; row: T };

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  label,
  searchable,
  filters,
  toolbarEnd,
  rowActions,
  onRowClick,
  rowLabel,
  groupBy,
  renderGroup,
  groupSort,
  pageSize = 25,
  loadMore,
  empty,
  loading,
  error,
  onRetry,
  initialSort,
  activeKey,
  className,
  frameless,
  virtualizeAbove = 100,
}: DataTableProps<T>) {
  const [query, setQuery] = useState('');
  const [sorting, setSorting] = useState<SortingState>(initialSort ? [{ id: initialSort.key, desc: initialSort.dir === 'desc' }] : []);
  const [page, setPage] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!searchable?.shortcut) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented) return;
      if (isTypingTarget(e.target)) return;
      if (document.querySelector('[role="dialog"][data-state="open"], [role="menu"][data-state="open"]')) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [searchable?.shortcut]);

  const data = useMemo<Boxed<T>[]>(() => rows.map((v) => ({ v })), [rows]);
  // Alignment reads the data once per data change: numbers right, everything else left.
  const aligns = useMemo(() => new Map(columns.map((c) => [c.key, columnAlign(c, rows)])), [columns, rows]);
  const alignOf = (c: Column<T>): CellAlign => aligns.get(c.key) ?? 'left';
  const nameKey = nameColumn(columns);
  const defs = useMemo<ColumnDef<Features, Boxed<T>>[]>(
    () => [
      ...columns.map(
        (c): ColumnDef<Features, Boxed<T>> => ({
          id: c.key,
          accessorFn: (r: Boxed<T>) => c.sortValue?.(r.v) ?? undefined,
          // A missing value sorts last in both directions.
          sortUndefined: 'last',
          enableSorting: c.sortValue != null,
          // Text sorts A to Z first; numbers, dates and durations biggest or newest first.
          sortDescFirst: sortsDescFirst(c, aligns.get(c.key) ?? 'left'),
          sortFn: (a, b, id) => compareValues(a.getValue(id), b.getValue(id)),
          enableGlobalFilter: false,
        }),
      ),
      {
        id: SEARCH,
        accessorFn: (r: Boxed<T>) => columns.map((c) => c.text?.(r.v) ?? '').join(' ').toLowerCase(),
        enableSorting: false,
      },
    ],
    [columns, aligns],
  );

  const table = useTable({
    features,
    columns: defs,
    data,
    state: { sorting, globalFilter: query },
    onSortingChange: (u) => setSorting((old) => functionalUpdate(u, old)),
    onGlobalFilterChange: () => undefined,
    enableSortingRemoval: false,
    getColumnCanGlobalFilter: (col) => col.id === SEARCH,
    globalFilterFn: (row, id, value) => matchesQuery(String(row.getValue(id) ?? ''), String(value ?? '')),
  });

  const visible = useMemo(() => {
    const out = table.getRowModel().rows.map((r) => r.original.v);
    if (!groupBy) return out;
    // Grouping keeps the sorted order inside each group and first-seen order between groups.
    const order: string[] = [];
    const byGroup = new Map<string, T[]>();
    for (const r of out) {
      const g = groupBy(r);
      const list = byGroup.get(g);
      if (list) list.push(r);
      else {
        byGroup.set(g, [r]);
        order.push(g);
      }
    }
    if (groupSort) order.sort(groupSort);
    return order.flatMap((g) => byGroup.get(g) ?? []);
    // table.getRowModel() is memoised on rows, sorting and the query.
  }, [table, data, sorting, query, groupBy, groupSort]);

  const size = pageSize === false ? Number.POSITIVE_INFINITY : pageSize;
  const pages = Math.max(1, Math.ceil(visible.length / size));
  const current = Math.min(page, pages - 1);
  useEffect(() => setPage(0), [query, sorting]);
  const pageRows = pageSize === false ? visible : visible.slice(current * size, current * size + size);

  const groupRows = useMemo(() => {
    if (!groupBy) return null;
    const m = new Map<string, T[]>();
    for (const r of visible) {
      const g = groupBy(r);
      const list = m.get(g);
      if (list) list.push(r);
      else m.set(g, [r]);
    }
    return m;
  }, [visible, groupBy]);

  const items = useMemo<DisplayItem<T>[]>(() => {
    const out: DisplayItem<T>[] = [];
    let last: string | null = null;
    for (const r of pageRows) {
      const g = groupBy?.(r) ?? null;
      if (g != null && g !== last && renderGroup) out.push({ kind: 'group', key: `g:${g}`, rows: groupRows?.get(g) ?? [] });
      last = g;
      out.push({ kind: 'row', key: rowKey(r), row: r });
    }
    return out;
  }, [pageRows, groupBy, renderGroup, groupRows, rowKey]);

  const ready = !loading && error == null && rows.length > 0 && visible.length > 0;
  const virtual = ready && items.length > virtualizeAbove;
  const virtualizer = useVirtualizer({
    count: virtual ? items.length : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => {
      const v = scrollRef.current ? parseFloat(getComputedStyle(scrollRef.current).getPropertyValue('--row-height')) : NaN;
      return Number.isFinite(v) ? v : 40;
    },
    getItemKey: (i) => items[i]?.key ?? i,
    overscan: 8,
    // A first paint (and server render) shows the top of the list before the container is measured.
    initialRect: { width: 0, height: 640 },
    enabled: virtual,
  });

  const sortOf = (key: string): 'asc' | 'desc' | null => {
    const s = sorting.find((x) => x.id === key);
    return s ? (s.desc ? 'desc' : 'asc') : null;
  };
  const toggleSort = (c: Column<T>) => {
    if (!c.sortValue) return;
    table.getColumn(c.key)?.toggleSorting();
  };

  const colCount = columns.length + (rowActions ? 1 : 0);
  const hasToolbar = searchable != null || filters != null || toolbarEnd != null;
  const gutterL = 'pl-5';
  const gutterR = 'pr-5';

  const onRowKey = (e: KeyboardEvent<HTMLTableRowElement>, row: T) => {
    if (!onRowClick || e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRowClick(row);
    }
  };

  const renderItem = (item: DisplayItem<T>, index?: number) => {
    const measure = virtual ? { 'data-index': index, ref: virtualizer.measureElement } : {};
    if (item.kind === 'group') {
      return (
        <tr key={item.key} data-group-header="" data-group={item.key.slice(2)} className="h-row bg-sunken/60" {...measure}>
          <td colSpan={colCount} className="py-0 pl-5 pr-3">
            {renderGroup?.(item.key.slice(2), item.rows)}
          </td>
        </tr>
      );
    }
    const r = item.row;
    const active = activeKey != null && item.key === activeKey;
    return (
      <tr
        key={item.key}
        data-row=""
        tabIndex={onRowClick ? 0 : undefined}
        aria-label={onRowClick && rowLabel ? rowLabel(r) : undefined}
        onClick={onRowClick ? () => onRowClick(r) : undefined}
        onKeyDown={onRowClick ? (e) => onRowKey(e, r) : undefined}
        data-active={active || undefined}
        className={cn(
          REVEAL_GROUP,
          tableClass.rowBase,
          '-outline-offset-2',
          onRowClick && 'cursor-default',
          active ? tableClass.selected : tableClass.rowHover,
        )}
        {...measure}
      >
        {columns.map((c, ci) => {
          const right = alignOf(c) === 'right';
          return (
            <td
              key={c.key}
              data-column={c.key}
              className={cn(
                'py-1.5',
                tableClass.cell,
                c.key === nameKey && tableClass.name,
                ci === 0 ? gutterL : 'pl-4',
                ci === columns.length - 1 && !rowActions && gutterR,
                c.grow ? 'w-full max-w-0' : 'w-px whitespace-nowrap',
                right && tableClass.numeric,
                c.hideBelow && HIDE[c.hideBelow],
                c.className,
              )}
            >
              {c.cell(r)}
            </td>
          );
        })}
        {rowActions && (
          <td className={cn('w-px whitespace-nowrap py-1 pl-3', gutterR)} onClick={(e) => e.stopPropagation()}>
            <RevealSlot className="justify-end">{rowActions(r)}</RevealSlot>
          </td>
        )}
      </tr>
    );
  };

  let body: ReactNode;
  if (loading) {
    body = Array.from({ length: 6 }, (_, i) => (
      <tr key={`sk${i}`} className="h-row">
        {columns.map((c, ci) => (
          <td key={c.key} className={cn('py-2', ci === 0 ? gutterL : 'pl-4', ci === columns.length - 1 && !rowActions && gutterR, c.hideBelow && HIDE[c.hideBelow])}>
            <Skeleton className="h-3" style={{ width: c.grow ? `${50 + ((i * 13) % 35)}%` : '56px' }} />
          </td>
        ))}
        {rowActions && <td className={gutterR} />}
      </tr>
    ));
  } else if (error != null) {
    body = (
      <tr>
        <td colSpan={colCount} className="px-5">
          <ErrorState error={error} onRetry={onRetry} />
        </td>
      </tr>
    );
  } else if (!rows.length) {
    body = (
      <tr>
        <td colSpan={colCount} className="px-5">
          {empty ?? <EmptyState title="Nothing here yet" />}
        </td>
      </tr>
    );
  } else if (!visible.length) {
    body = (
      <tr>
        <td colSpan={colCount} className="px-5">
          <EmptyState
            icon={<SearchX />}
            title="No matches"
            description={query ? `Nothing matches "${query.trim()}" with the current filters.` : 'Nothing matches the current filters.'}
            action={
              query ? (
                <Button size="sm" variant="ghost" onClick={() => setQuery('')}>
                  Clear search
                </Button>
              ) : undefined
            }
          />
        </td>
      </tr>
    );
  } else if (virtual) {
    const vItems = virtualizer.getVirtualItems();
    const top = vItems[0]?.start ?? 0;
    const bottom = virtualizer.getTotalSize() - (vItems[vItems.length - 1]?.end ?? 0);
    body = (
      <>
        {top > 0 && <tr aria-hidden style={{ height: top }} />}
        {vItems.map((v) => {
          const item = items[v.index];
          return item ? <Fragment key={item.key}>{renderItem(item, v.index)}</Fragment> : null;
        })}
        {bottom > 0 && <tr aria-hidden style={{ height: bottom }} />}
      </>
    );
  } else {
    body = items.map((item) => <Fragment key={item.key}>{renderItem(item)}</Fragment>);
  }

  const showPager = !loading && error == null && pageSize !== false && visible.length > size;
  const showFooter = ready && columns.some((c) => c.footer);
  const showLoadMore = !loading && error == null && loadMore?.more;

  return (
    <div className={cn('flex min-w-0 flex-col', !frameless && 'overflow-hidden rounded-card border border-border bg-surface shadow-elevation-flat', className)}>
      {hasToolbar && (
        <div className="flex min-h-(--filter-bar-height) flex-wrap items-center gap-2 border-b border-border-subtle px-5 py-1.5">
          {searchable && (
            <Input
              ref={searchRef}
              size="sm"
              type="search"
              aria-label={searchable.placeholder}
              placeholder={searchable.placeholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && query) {
                  e.preventDefault();
                  e.stopPropagation();
                  setQuery('');
                }
              }}
              icon={<Search />}
              trailing={searchable.shortcut && !query ? <Kbd keys={['/']} /> : undefined}
              wrapperClassName="w-full sm:w-64"
            />
          )}
          {filters && <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2 overflow-x-auto">{filters}</div>}
          {toolbarEnd && <div className="ml-auto flex items-center gap-2 text-caption text-fg-subtle">{toolbarEnd}</div>}
        </div>
      )}
      <div ref={scrollRef} data-list="" className={cn('min-w-0 overflow-x-auto', virtual && 'max-h-[70vh] overflow-y-auto')}>
        <table className="w-full border-collapse text-left" aria-label={label} aria-busy={loading || undefined} aria-rowcount={virtual ? items.length : undefined}>
          <thead className={cn(virtual && 'sticky top-0 z-(--z-sticky) bg-surface')}>
            <tr className={tableClass.headRow}>
              {columns.map((c, ci) => {
                const right = alignOf(c) === 'right';
                const sorted = sortOf(c.key);
                return (
                  <th
                    key={c.key}
                    scope="col"
                    aria-sort={sorted ? (sorted === 'asc' ? 'ascending' : 'descending') : undefined}
                    className={cn(
                      tableClass.head,
                      'py-0',
                      ci === 0 ? gutterL : 'pl-4',
                      ci === columns.length - 1 && !rowActions && gutterR,
                      right && 'text-right',
                      c.hideBelow && HIDE[c.hideBelow],
                    )}
                  >
                    {c.srHeader ? (
                      <span className="sr-only">{c.header}</span>
                    ) : c.sortValue ? (
                      <TableSortButton dir={sorted} right={right} onClick={() => toggleSort(c)}>
                        {c.header}
                      </TableSortButton>
                    ) : (
                      c.header
                    )}
                  </th>
                );
              })}
              {rowActions && (
                <th scope="col" className={cn('w-px', gutterR)}>
                  <span className="sr-only">Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>{body}</tbody>
          {showFooter && (
            <tfoot>
              <tr className={tableClass.footRow}>
                {columns.map((c, ci) => (
                  <td
                    key={c.key}
                    data-column={c.key}
                    className={cn(
                      'py-1.5',
                      ci === 0 ? gutterL : 'pl-4',
                      ci === columns.length - 1 && !rowActions && gutterR,
                      c.grow ? 'w-full max-w-0' : 'w-px whitespace-nowrap',
                      alignOf(c) === 'right' && tableClass.numeric,
                      c.hideBelow && HIDE[c.hideBelow],
                    )}
                  >
                    {c.footer?.(visible)}
                  </td>
                ))}
                {rowActions && <td className={gutterR} />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {(showPager || showLoadMore) && (
        <div className="flex items-center justify-end gap-2 border-t border-border-subtle px-5 py-2 text-caption text-fg-subtle">
          {showPager && (
            <>
              <span className="num">
                {formatCount(current * size + 1)}-{formatCount(Math.min(visible.length, (current + 1) * size))} of {formatCount(visible.length)}
                {loadMore ? ' loaded' : ''}
              </span>
              <IconButton label="Previous page" icon={<ChevronLeft />} size="xs" disabled={current === 0} onClick={() => setPage(current - 1)} />
              <IconButton label="Next page" icon={<ChevronRight />} size="xs" disabled={current >= pages - 1} onClick={() => setPage(current + 1)} />
            </>
          )}
          {showLoadMore && loadMore && (
            <Button size="sm" variant="ghost" loading={loadMore.loading} onClick={loadMore.onLoad}>
              Load older
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
